import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Check, X, Loader2, ChevronRight, Trophy } from 'lucide-react';

const CATEGORY_META = {
  politics: { label: 'Politics', color: '#FF007F' },
  sports:   { label: 'Sports',   color: '#39FF14' },
  movies:   { label: 'Movies',   color: '#FFD700' },
};

const TriviaGame = ({ categories, passThreshold = 4, onWin, onExit }) => {
  const [phase, setPhase] = useState('pick'); // 'pick' | 'play' | 'result'
  const [category, setCategory] = useState(null);
  const [qIdx, setQIdx] = useState(0);
  const [picked, setPicked] = useState(null); // index of picked option for current q
  const [score, setScore] = useState(0);
  const [completedCats, setCompletedCats] = useState(new Set()); // categories the user has passed

  const questions = useMemo(() => (category ? categories[category] || [] : []), [category, categories]);
  const totalQ = questions.length;
  const currentQ = questions[qIdx];

  const startCategory = (cat) => {
    setCategory(cat);
    setQIdx(0);
    setPicked(null);
    setScore(0);
    setPhase('play');
  };

  const pick = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    if (idx === currentQ.answer) setScore((s) => s + 1);
  };

  const nextQ = () => {
    if (qIdx + 1 >= totalQ) {
      setPhase('result');
    } else {
      setQIdx((i) => i + 1);
      setPicked(null);
    }
  };

  const finishCategory = () => {
    const passed = score >= passThreshold;
    if (passed) {
      const next = new Set(completedCats);
      next.add(category);
      setCompletedCats(next);
      // ANY category cleared = trigger win
      onWin?.();
    } else {
      // Back to picker
      setCategory(null);
      setPhase('pick');
      setQIdx(0);
      setPicked(null);
      setScore(0);
    }
  };

  if (!categories) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#FF007F] animate-spin" /></div>;
  }

  // ---- Phase: pick category ----
  if (phase === 'pick') {
    return (
      <div className="space-y-4" data-testid="trivia-pick">
        <div className="text-center mb-2">
          <p className="text-gray-400 text-sm">
            Pick a category. Score {passThreshold} or more out of {Object.values(categories)[0]?.length || 5} to win.
          </p>
        </div>
        {Object.keys(categories).map((cat) => {
          const meta = CATEGORY_META[cat] || { label: cat, color: '#FFFFFF' };
          const done = completedCats.has(cat);
          return (
            <button
              key={cat}
              onClick={() => !done && startCategory(cat)}
              disabled={done}
              className={`w-full p-5 rounded-2xl border text-left transition-all group ${
                done
                  ? 'bg-[#39FF14]/8 border-[#39FF14]/30 cursor-default'
                  : 'bg-white/5 border-white/10 hover:border-[#FF007F]/40 hover:bg-[#FF007F]/5'
              }`}
              data-testid={`trivia-cat-${cat}`}
              style={!done ? { borderLeftColor: meta.color, borderLeftWidth: 4 } : {}}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-base font-bold font-['Outfit']">{meta.label}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{(categories[cat] || []).length} questions</p>
                </div>
                {done ? (
                  <span className="inline-flex items-center gap-1.5 text-[#39FF14] text-xs font-bold uppercase tracking-wider">
                    <Trophy className="w-4 h-4" /> Won
                  </span>
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white" />
                )}
              </div>
            </button>
          );
        })}
        <button
          onClick={onExit}
          className="w-full text-xs text-gray-500 hover:text-gray-300 inline-flex items-center justify-center gap-1.5 pt-2"
          data-testid="trivia-quit"
        >
          <X className="w-3 h-3" /> Quit game
        </button>
      </div>
    );
  }

  // ---- Phase: play ----
  if (phase === 'play' && currentQ) {
    const correct = picked !== null && picked === currentQ.answer;
    const wrong = picked !== null && picked !== currentQ.answer;
    return (
      <div className="space-y-5" data-testid="trivia-play">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
            {CATEGORY_META[category]?.label} · Q{qIdx + 1} of {totalQ}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-[#FFD700] font-bold">
            Score: {score}
          </span>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.04] border border-white/10">
          <p className="text-white text-lg font-semibold font-['Outfit'] leading-snug" data-testid="trivia-question">
            {currentQ.q}
          </p>
        </div>
        <div className="space-y-2">
          {currentQ.options.map((opt, idx) => {
            const isPickedCorrect = picked === idx && idx === currentQ.answer;
            const isPickedWrong = picked === idx && idx !== currentQ.answer;
            const isCorrectAnswer = picked !== null && idx === currentQ.answer;
            return (
              <button
                key={`${category}-${qIdx}-${idx}`}
                onClick={() => pick(idx)}
                disabled={picked !== null}
                className={`w-full p-3.5 rounded-xl border text-left text-sm transition-all flex items-center justify-between gap-3 ${
                  isPickedCorrect ? 'bg-[#39FF14]/15 border-[#39FF14]/50 text-white'
                    : isPickedWrong ? 'bg-[#FF3B3B]/15 border-[#FF3B3B]/40 text-white'
                    : isCorrectAnswer ? 'bg-[#39FF14]/8 border-[#39FF14]/30 text-white'
                    : 'bg-white/5 border-white/10 text-gray-200 hover:border-[#FF007F]/40 hover:bg-[#FF007F]/5'
                } disabled:cursor-not-allowed`}
                data-testid={`trivia-option-${idx}`}
              >
                <span>{opt}</span>
                {isPickedCorrect || (isCorrectAnswer && picked !== null) ? <Check className="w-4 h-4 text-[#39FF14]" /> : null}
                {isPickedWrong ? <X className="w-4 h-4 text-[#FF3B3B]" /> : null}
              </button>
            );
          })}
        </div>
        {picked !== null && (
          <Button
            onClick={nextQ}
            className={`w-full h-12 text-white font-bold tracking-wider rounded-xl ${
              correct ? 'bg-[#39FF14] hover:bg-[#2dd60f] text-black' : 'bg-[#FF007F] hover:bg-[#CC0066]'
            }`}
            data-testid="trivia-next"
          >
            {qIdx + 1 >= totalQ ? 'SEE RESULTS' : 'NEXT QUESTION'}
          </Button>
        )}
      </div>
    );
  }

  // ---- Phase: result ----
  const passed = score >= passThreshold;
  return (
    <div className="text-center space-y-4" data-testid="trivia-result">
      <div className={`p-6 rounded-3xl border ${passed ? 'bg-[#39FF14]/10 border-[#39FF14]/30' : 'bg-[#FF3B3B]/10 border-[#FF3B3B]/30'}`}>
        <p className={`text-3xl font-extrabold font-['Outfit'] ${passed ? 'text-[#39FF14]' : 'text-[#FF3B3B]'}`}>
          {score}/{totalQ}
        </p>
        <p className="text-white font-bold mt-2">
          {passed ? 'You cleared it!' : 'Not quite — try another category.'}
        </p>
      </div>
      <Button
        onClick={finishCategory}
        className={`w-full h-12 font-bold tracking-wider rounded-xl ${passed ? 'bg-[#FFD700] hover:bg-[#e6c200] text-black' : 'bg-white/10 hover:bg-white/15 text-white border border-white/15'}`}
        data-testid="trivia-finish"
      >
        {passed ? 'CLAIM REWARD' : 'PICK ANOTHER CATEGORY'}
      </Button>
      <button onClick={onExit} className="w-full text-xs text-gray-500 hover:text-gray-300" data-testid="trivia-quit-result">
        Quit game
      </button>
    </div>
  );
};

export default TriviaGame;
